import { useEffect, useState } from "react";
import { listAssignableUsers } from "../lib/alertTriageClient.js";
import ConfirmDialog from "./ConfirmDialog.jsx";
import Select from "./Select.jsx";
import { DIALOG } from "../lib/analytics/surfaceVocabulary.js";

export function useInvestigationAssignees(enabled, contextKey) {
    const [state, setState] = useState({ users: [], loading: false, error: false });
    const [attempt, setAttempt] = useState(0);
    useEffect(() => {
        if (!enabled) return;
        let live = true;
        setState({ users: [], loading: true, error: false });
        listAssignableUsers().then((users) => { if (live) setState({ users, loading: false, error: false }); }, () => { if (live) setState({ users: [], loading: false, error: true }); });
        return () => { live = false; };
    }, [enabled, contextKey, attempt]);
    return { ...state, retry: () => setAttempt((n) => n + 1) };
}
export const assigneeLabel = (id, users) => id ? users.find((u) => u.userId === id)?.email || "Assigned analyst" : "Unassigned";
export const VERDICT_OPTIONS = [{ value: "", label: "No determination" }, { value: "true_positive", label: "True positive" }, { value: "false_positive", label: "False positive" }];
const STATUS_OPTIONS = [{ value: "open", label: "Open" }, { value: "closed", label: "Closed" }];

export default function InvestigationMetadata({ record, collection: c, assignees, onCancel, onSaved, onDeleted, includeVerdict = true }) {
    const [draft, setDraft] = useState({});
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [deleteConfirmation, setDeleteConfirmation] = useState("");
    const creating = !record;
    const value = (field) => Object.hasOwn(draft, field) ? draft[field] : record?.[field] ?? "";
    const change = (field, next) => setDraft((prior) => ({ ...prior, [field]: next }));
    const title = value("title");
    const locked = c.busy || c.hasPending;
    return <><form className="investigation-metadata" aria-label={creating ? "Create investigation" : "Edit investigation"} aria-busy={c.busy} onSubmit={async (event) => {
        event.preventDefault();
        if (locked || !title.trim()) return;
        const changes = { ...draft, ...(creating || Object.hasOwn(draft, "title") ? { title: title.trim() } : {}) };
        const saved = creating ? await c.createInvestigation(changes) : await c.saveMetadata(record, changes);
        if (saved) onSaved?.();
    }}>
        <h2>{creating ? "New investigation" : "Edit investigation"}</h2>
        <fieldset disabled={locked}>
            <label>Name<input autoFocus required maxLength={200} value={title} onChange={(e) => change("title", e.target.value)} /></label>
            <label>Summary<textarea maxLength={4000} rows={2} value={value("summary")} onChange={(e) => change("summary", e.target.value)} /></label>
            <div className="investigation-metadata-fields">
                {!creating && <label>Status<Select value={value("status") || "open"} onChange={(v) => change("status", v)} options={STATUS_OPTIONS} /></label>}
                <label>Assignee<Select disabled={assignees.loading || assignees.error} value={value("ownerUserId") || ""} onChange={(v) => change("ownerUserId", v || null)} options={[
                    { value: "", label: "Unassigned" },
                    ...(value("ownerUserId") && !assignees.users.some((u) => u.userId === value("ownerUserId")) ? [{ value: value("ownerUserId"), label: "Current assignee (unavailable)" }] : []),
                    ...assignees.users.map((u) => ({ value: u.userId, label: u.email })),
                ]} /></label>
                {!creating && includeVerdict && <label>Determination<Select value={value("verdict") || ""} onChange={(v) => change("verdict", v || null)} options={VERDICT_OPTIONS} /></label>}
            </div>
            {assignees.loading && <p role="status" aria-busy="true">Loading assignees...</p>}
            {assignees.error && <p role="alert">Assignees could not be loaded. <button type="button" onClick={assignees.retry}>Retry assignees</button></p>}
            {creating && <p>Saved immediately. You can add findings from Discovery afterward.</p>}
            {draft.status === "closed" && record?.status !== "closed" && <p>Closed investigations keep their findings and cannot receive more until reopened.</p>}
            <div className="investigation-actions">
                <button type="submit" disabled={!title.trim() || (!creating && !Object.keys(draft).length)}>{creating ? "Create investigation" : "Save changes"}</button>
                <button type="button" onClick={onCancel}>Cancel</button>
                {!creating && record.canDelete && <button type="button" className="danger investigation-delete-action" onClick={() => { setDeleteConfirmation(""); setDeleteOpen(true); }}>Delete investigation</button>}
            </div>
        </fieldset>
    </form>
    <ConfirmDialog analyticsId={DIALOG.INVESTIGATION_DELETE} open={deleteOpen} title="Delete investigation?" eyebrow="Permanent action" confirmLabel="Delete investigation" destructive className="investigation-delete-dialog"
        confirmDisabled={locked || deleteConfirmation !== record?.title}
        onCancel={() => { if (!locked) setDeleteOpen(false); }}
        onConfirm={async () => {
            if (locked || deleteConfirmation !== record?.title) return;
            const deleted = await c.deleteInvestigation(record);
            if (deleted) { setDeleteOpen(false); onDeleted?.(); }
        }}
        body={<div className="investigation-delete-confirmation">
            <p>This permanently deletes the investigation, its collected finding references, and analyst log. The deletion itself is retained in an immutable audit record.</p>
            <p>The underlying findings, traces, and captured data are not deleted.</p>
            <label>Type <strong>{record?.title}</strong> to confirm<input autoFocus value={deleteConfirmation} onChange={(event) => setDeleteConfirmation(event.target.value)} /></label>
        </div>} />
    </>;
}
